/**
 * @pryme/ui — shared portal toolkit: same-origin API client with typed errors,
 * WebAuthn helpers (registration, login, purpose-bound step-up) and small React components.
 */
import { currencyExponent, minorToDecimal } from '@pryme/protocol';
import { startAuthentication, startRegistration } from '@simplewebauthn/browser';
import { createElement as h, useCallback, useEffect, useRef, useState, type ReactNode } from 'react';

// ------------------------------------------------------------------ API client
export class ApiError extends Error {
  constructor(public code: string, message: string, public status: number) {
    super(message);
  }
}

/** Human-readable explanations for protocol error codes (sandbox-friendly). */
export const ERROR_TEXT: Record<string, string> = {
  identity_not_verified: 'You need an active, verified Pryme identity for this.',
  verification_pending: 'Verification is still in progress.',
  verification_denied: 'Verification was cancelled or denied. No identity was activated.',
  verification_expired: 'This verification attempt expired. Please start again.',
  verification_provider_unavailable: 'The identity provider is unavailable right now. Nothing was activated; try again or choose another method.',
  unsupported_verification_scheme: 'That verification method is not available here.',
  assurance_insufficient: 'The verification level was too low.',
  duplicate_identity_review_required: 'We could not safely determine whether you already have a Pryme ID. This needs manual review; no new identity was created.',
  invalid_signature: 'A signature or proof did not verify.',
  replay_detected: 'This request or link was already used.',
  expired_presentation: 'This request or link has expired.',
  step_up_required: 'Please confirm with your passkey.',
  credential_revoked: 'This passkey or key has been revoked.',
  consent_required: 'Consent is required.',
  handle_unavailable: 'That handle is not available.',
  identity_recovery_required: 'No Pryme ID is bound to this eID. Create a new Pryme ID instead.',
  rate_limited: 'Too many attempts. Please wait a moment.',
  temporarily_unavailable: 'Pryme is temporarily unavailable. High-risk actions fail closed.',
  forbidden: 'You do not have permission to do that.',
  not_found: 'Not found.',
  invalid_request: 'The request was invalid.',
  invalid_state: 'That action is not allowed in the current state.',
  unverified_domain: 'A verified domain is required.',
};

export function explain(e: unknown): string {
  if (e instanceof ApiError) return `${ERROR_TEXT[e.code] ?? e.code}${e.message && e.message !== e.code && !ERROR_TEXT[e.code]?.includes(e.message) ? ` (${e.message})` : ''}`;
  if (e instanceof Error) {
    if (e.name === 'NotAllowedError') return 'The passkey prompt was cancelled or timed out.';
    return e.message;
  }
  return String(e);
}

let API_BASE = '';
/**
 * Call a Pryme API on another origin (the developer portal at dev.pryme.id talks to
 * api.sandbox.pryme.id or api.pryme.id). Same-site, so the session cookie travels with credentials.
 */
export function setApiBase(base: string) {
  API_BASE = base.replace(/\/$/, '');
}
/** Absolute URL for an API path under the current API base (unchanged when same-origin). */
export const apiUrl = (path: string) => (API_BASE && path.startsWith('/') ? `${API_BASE}${path}` : path);

export async function api<T = unknown>(path: string, init: { method?: string; body?: unknown; stepUp?: string } = {}): Promise<T> {
  const headers: Record<string, string> = { accept: 'application/json' };
  if (init.body !== undefined || (init.method && init.method !== 'GET')) headers['content-type'] = 'application/json';
  if (init.stepUp) headers['pryme-step-up'] = init.stepUp;
  const res = await fetch(apiUrl(path), {
    method: init.method ?? (init.body !== undefined ? 'POST' : 'GET'),
    headers,
    credentials: API_BASE ? 'include' : 'same-origin',
    body: init.body !== undefined ? JSON.stringify(init.body) : init.method && init.method !== 'GET' ? '{}' : undefined,
  });
  const text = await res.text();
  const json = text ? JSON.parse(text) : {};
  if (!res.ok) throw new ApiError(json.error?.code ?? 'temporarily_unavailable', json.error?.message ?? res.statusText, res.status);
  return json as T;
}

// ------------------------------------------------------------------ WebAuthn
export async function loginWithPasskey() {
  const opts = await api<Record<string, unknown> & { loginBinding: string }>('/v1/auth/passkey/options', { method: 'POST' });
  const { loginBinding, ...optionsJSON } = opts;
  const response = await startAuthentication({ optionsJSON: optionsJSON as never });
  return api<{ ok: true; environment: string }>('/v1/auth/passkey/verify', { body: { response, loginBinding } });
}

export async function registerPasskey(optionsPath: string, verifyPath: string, extra: Record<string, unknown>, label?: string) {
  const optionsJSON = await api<Record<string, unknown>>(optionsPath, { body: extra });
  const response = await startRegistration({ optionsJSON: optionsJSON as never });
  return api<Record<string, unknown>>(verifyPath, { body: { ...extra, response, label } });
}

/** Purpose-bound passkey step-up. Returns a one-time token to send as `Pryme-Step-Up`. */
export async function stepUp(purpose: string): Promise<string> {
  const optionsJSON = await api<Record<string, unknown>>('/v1/auth/step-up/options', { body: { purpose } });
  const response = await startAuthentication({ optionsJSON: optionsJSON as never });
  const r = await api<{ stepUpToken: string }>('/v1/auth/step-up/verify', { body: { purpose, response } });
  return r.stepUpToken;
}

/** Perform a sensitive call that needs a passkey step-up for `purpose`. */
export async function withStepUp<T>(purpose: string, path: string, init: { method?: string; body?: unknown } = {}): Promise<T> {
  const token = await stepUp(purpose);
  return api<T>(path, { ...init, method: init.method ?? 'POST', stepUp: token });
}

// ------------------------------------------------------------------ hooks
export function useApi<T>(path: string | null, deps: unknown[] = []) {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [loading, setLoading] = useState(!!path);
  const reload = useCallback(async () => {
    if (!path) return;
    setLoading(true);
    try {
      setData(await api<T>(path));
      setError(null);
    } catch (e) {
      setError(e);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [path, ...deps]);
  useEffect(() => {
    void reload();
  }, [reload]);
  return { data, error, loading, reload, setData };
}

export function usePolling(fn: () => void, ms: number, enabled = true) {
  const ref = useRef(fn);
  ref.current = fn;
  useEffect(() => {
    if (!enabled) return;
    const id = setInterval(() => {
      if (document.visibilityState === 'visible') ref.current();
    }, ms);
    return () => clearInterval(id);
  }, [ms, enabled]);
}

// ------------------------------------------------------------------ formatting
/**
 * An amount in integer minor units, for people. Exact: the decimal digits come from the one currency
 * registry (`@pryme/protocol`, mirroring the Rust core), never from a guess or binary floats, so what
 * is shown is what Pryme signs and enforces (security review 2026-11, H2). Never throws: a value Pryme
 * would not accept (an unsupported currency, a non-integer) is shown raw and marked.
 */
export function formatMinor(minor: number, currency: string) {
  const decimal = minorToDecimal(minor, currency);
  if (decimal === null) return `${String(minor)} ${String(currency).slice(0, 12)} (minor units, unsupported currency)`;
  const exp = currencyExponent(currency)!;
  try {
    // Intl formats a decimal string exactly (no float rounding).
    return new Intl.NumberFormat('en-DK', { style: 'currency', currency, minimumFractionDigits: exp, maximumFractionDigits: exp })
      .format(decimal as unknown as number);
  } catch {
    return `${decimal} ${currency}`;
  }
}

export function formatTime(iso: string | null | undefined) {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString();
}

export function relativeTime(iso: string | null | undefined) {
  if (!iso) return '—';
  const s = Math.round((Date.parse(iso) - Date.now()) / 1000);
  const rtf = new Intl.RelativeTimeFormat('en', { numeric: 'auto' });
  const abs = Math.abs(s);
  if (abs < 60) return rtf.format(s, 'second');
  if (abs < 3600) return rtf.format(Math.round(s / 60), 'minute');
  if (abs < 86400) return rtf.format(Math.round(s / 3600), 'hour');
  return rtf.format(Math.round(s / 86400), 'day');
}

// ------------------------------------------------------------------ components (no JSX so this package needs no build step)
export type Tone = 'ok' | 'warn' | 'bad' | 'neutral' | 'sandbox' | 'brand';

export function Badge(props: { tone?: Tone; children: ReactNode; title?: string; icon?: IconName }) {
  return h(
    'span',
    { className: `p-badge ${props.tone && props.tone !== 'brand' ? props.tone : ''}`, title: props.title },
    props.icon ? h(Icon, { name: props.icon, size: 13 }) : null,
    props.children,
  );
}

// ------------------------------------------------------------------ icons
/** A small line-icon set (24px grid, 1.75 stroke). Icons support text; they never replace it. */
const ICONS = {
  check: ['M5 12.5l4.5 4.5L19 7.5'],
  shield: ['M12 3l7.5 3v5.6c0 4.3-3.1 8.1-7.5 9.4-4.4-1.3-7.5-5.1-7.5-9.4V6L12 3z'],
  shieldCheck: ['M12 3l7.5 3v5.6c0 4.3-3.1 8.1-7.5 9.4-4.4-1.3-7.5-5.1-7.5-9.4V6L12 3z', 'M8.75 12.25l2.25 2.25 4.25-4.5'],
  lock: ['M6.5 10.5h11a1.5 1.5 0 0 1 1.5 1.5v7a1.5 1.5 0 0 1-1.5 1.5h-11A1.5 1.5 0 0 1 5 19v-7a1.5 1.5 0 0 1 1.5-1.5z', 'M8 10.5V7.75a4 4 0 0 1 8 0v2.75'],
  key: ['M8.5 19a4.5 4.5 0 1 1 0-9 4.5 4.5 0 0 1 0 9z', 'M11.7 11.3L19.5 3.5', 'M16.5 6.5l2.5 2.5', 'M14.5 8.5l2 2'],
  clock: ['M12 21a9 9 0 1 1 0-18 9 9 0 0 1 0 18z', 'M12 7.5V12l3 2'],
  globe: ['M12 21a9 9 0 1 1 0-18 9 9 0 0 1 0 18z', 'M3.5 9h17M3.5 15h17', 'M12 3c2.4 2.4 3.6 5.4 3.6 9s-1.2 6.6-3.6 9c-2.4-2.4-3.6-5.4-3.6-9S9.6 5.4 12 3z'],
  user: ['M12 12a4 4 0 1 0 0-8 4 4 0 0 0 0 8z', 'M4.5 20.5c1.3-3.6 4.1-5.5 7.5-5.5s6.2 1.9 7.5 5.5'],
  users: ['M9 11a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7z', 'M2.5 20c1.1-3.2 3.6-5 6.5-5s5.4 1.8 6.5 5', 'M16 4.3a3.5 3.5 0 0 1 0 6.4', 'M18 15.4c1.6.7 2.8 2.3 3.5 4.6'],
  agent: ['M6.5 8h11A2.5 2.5 0 0 1 20 10.5v6a2.5 2.5 0 0 1-2.5 2.5h-11A2.5 2.5 0 0 1 4 16.5v-6A2.5 2.5 0 0 1 6.5 8z', 'M12 8V4.5', 'M9.25 12.75v1', 'M14.75 12.75v1', 'M2 12.5v2.5M22 12.5v2.5'],
  building: ['M4 20.5h16', 'M6 20.5V5.5L12 3l6 2.5v15', 'M9.5 8.5h.01M14.5 8.5h.01M9.5 12h.01M14.5 12h.01M9.5 15.5h.01M14.5 15.5h.01', 'M10.5 20.5v-2.5h3v2.5'],
  link: ['M10 14a4 4 0 0 0 5.66 0l3-3a4 4 0 1 0-5.66-5.66l-1 1', 'M14 10a4 4 0 0 0-5.66 0l-3 3a4 4 0 1 0 5.66 5.66l1-1'],
  eyeOff: ['M3 3l18 18', 'M10.6 5.1A9.7 9.7 0 0 1 12 5c5 0 8.5 4.5 9.5 7-.4 1-1.2 2.3-2.4 3.5M6.6 6.6C4.6 7.9 3.2 9.9 2.5 12c1 2.5 4.5 7 9.5 7 1.9 0 3.5-.6 4.9-1.5', 'M9.9 9.9a3 3 0 0 0 4.2 4.2'],
  alert: ['M12 3.5l9.5 16.5h-19L12 3.5z', 'M12 10v4.5', 'M12 17.25h.01'],
  info: ['M12 21a9 9 0 1 1 0-18 9 9 0 0 1 0 18z', 'M12 11v5.5', 'M12 7.75h.01'],
  arrowRight: ['M5 12h14', 'M13 6l6 6-6 6'],
  arrowLeft: ['M19 12H5', 'M11 6l-6 6 6 6'],
  external: ['M14 4h6v6', 'M20 4l-9 9', 'M18 14v5a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V7a1 1 0 0 1 1-1h5'],
  document: ['M7 3h7l5 5v12a1 1 0 0 1-1 1H7a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1z', 'M14 3v5h5', 'M9.5 13h6M9.5 16.5h6'],
  inbox: ['M3.5 13.5l2.6-7.8A1 1 0 0 1 7 5h10a1 1 0 0 1 .9.7l2.6 7.8', 'M3.5 13.5V19a1 1 0 0 0 1 1h15a1 1 0 0 0 1-1v-5.5h-5.25l-1 2.25h-4.5l-1-2.25H3.5z'],
  device: ['M8 3h8a1.5 1.5 0 0 1 1.5 1.5v15A1.5 1.5 0 0 1 16 21H8a1.5 1.5 0 0 1-1.5-1.5v-15A1.5 1.5 0 0 1 8 3z', 'M11 18h2'],
  activity: ['M3 12h4l2.5-6 5 12 2.5-6h4'],
  home: ['M4 10.5L12 4l8 6.5V20a1 1 0 0 1-1 1h-4.5v-6h-5v6H5a1 1 0 0 1-1-1v-9.5z'],
  handshake: ['M4 8.5h4l4 4', 'M20 8.5h-4l-5.5 5.5a1.5 1.5 0 0 0 2.1 2.1l1.4-1.4', 'M12.5 15.5l1.5 1.5a1.5 1.5 0 0 0 2.1-2.1', 'M14.5 13.5l2 2a1.5 1.5 0 0 0 2.1-2.1L16 10.8', 'M4 8.5V15l3.5 3.5'],
  code: ['M8.5 7L3.5 12l5 5', 'M15.5 7l5 5-5 5', 'M13.5 4.5l-3 15'],
  layers: ['M12 3l9 5-9 5-9-5 9-5z', 'M3 13l9 5 9-5'],
  x: ['M6 6l12 12', 'M18 6L6 18'],
  plus: ['M12 5v14', 'M5 12h14'],
  pause: ['M9 5v14', 'M15 5v14'],
  signature: ['M3.5 17.5c2.5 0 3.5-9 6-9 2 0 0 7 2.5 7 1.6 0 2-2.5 3.5-2.5 1.2 0 1.3 1.5 2.5 1.5h2.5', 'M3.5 20.5h17'],
} as const;
export type IconName = keyof typeof ICONS;

export function Icon(props: { name: IconName; size?: number; label?: string; className?: string; strokeWidth?: number }) {
  const size = props.size ?? 16;
  return h(
    'svg',
    {
      className: `p-icon ${props.className ?? ''}`,
      width: size,
      height: size,
      viewBox: '0 0 24 24',
      fill: 'none',
      stroke: 'currentColor',
      strokeWidth: props.strokeWidth ?? 1.75,
      strokeLinecap: 'round',
      strokeLinejoin: 'round',
      role: props.label ? 'img' : undefined,
      'aria-label': props.label,
      'aria-hidden': props.label ? undefined : true,
      focusable: 'false',
    },
    ICONS[props.name].map((d, i) => h('path', { key: i, d })),
  );
}

/** Sandbox tag for headers: always visible, never alarming. */
export function EnvironmentTag(props: { environment?: string | null }) {
  const production = props.environment === 'production';
  return h(
    'span',
    { className: `p-env-tag ${production ? 'production' : 'sandbox'}`, title: production ? 'Production trust environment' : 'Sandbox: test identities only' },
    h('span', { className: 'p-dot', 'aria-hidden': true }),
    production ? 'Production' : 'Sandbox',
  );
}

/** Trust environment of an identity or provider. Sandbox evidence is never presented as real verification. */
export function EnvironmentBadge(props: { environment?: string | null; category?: string | null }) {
  if (props.environment === 'production') {
    return h(Badge, { tone: 'ok', title: 'Production trust environment', children: props.category === 'document_kyc' ? 'Document-verified' : 'Production' });
  }
  const label = props.category === 'test_mock' ? 'Sandbox · offline test provider' : 'Sandbox · test identity';
  return h(Badge, { tone: 'sandbox', title: 'Sandbox: not a real government-verified identity', children: label });
}

export function Alert(props: { tone?: 'info' | 'ok' | 'warn' | 'bad'; children: ReactNode; role?: string }) {
  return h('div', { className: `p-alert ${props.tone ?? 'info'}`, role: props.role ?? (props.tone === 'bad' ? 'alert' : 'status') }, props.children);
}

export function ErrorAlert(props: { error: unknown }) {
  if (!props.error) return null;
  return h(Alert, { tone: 'bad', children: explain(props.error) });
}

export function Spinner(props: { label?: string }) {
  return h('span', { className: 'p-row', role: 'status', style: { gap: '0.5rem' } }, h('span', { className: 'p-spinner', 'aria-hidden': true }), props.label ? h('span', null, props.label) : null);
}

export function CopyButton(props: { value: string; label?: string }) {
  const [copied, setCopied] = useState(false);
  return h(
    'button',
    {
      type: 'button',
      className: 'p-btn secondary small',
      onClick: async () => {
        await navigator.clipboard.writeText(props.value);
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
      },
    },
    copied ? h(Icon, { name: 'check', size: 14 }) : null,
    copied ? 'Copied' : props.label ?? 'Copy',
  );
}

/** Slim band above the header on every sandbox page. Calm, but impossible to miss. */
export function SandboxStrip(props: { environment?: string }) {
  if (props.environment === 'production') return null;
  return h(
    'div',
    { className: 'p-envbar', role: 'note' },
    h('strong', null, 'Sandbox'),
    h('span', null, 'Test identities only. Not valid as real identity verification.'),
  );
}

/** The Pryme mark: a "P" with a full stop, echoing "Programmable. Verifiable. Me." */
export function LogoMark(props: { size?: number }) {
  const s = props.size ?? 26;
  return h(
    'svg',
    { className: 'p-logo-mark', width: s, height: s, viewBox: '0 0 28 28', 'aria-hidden': true, focusable: 'false' },
    h('rect', { width: 28, height: 28, rx: 7, fill: '#0f1a2a' }),
    h('path', { d: 'M10.5 20.5V7.5h5a4.25 4.25 0 0 1 0 8.5h-5', fill: 'none', stroke: '#fff', strokeWidth: 2.3, strokeLinecap: 'round', strokeLinejoin: 'round' }),
    h('circle', { cx: 19.6, cy: 20, r: 1.7, fill: '#7aa7e6' }),
  );
}

export function Logo(props: { suffix?: string }) {
  return h('span', { className: 'p-logo' }, h(LogoMark, {}), 'Pryme', props.suffix ? h('span', { className: 'p-logo-suffix' }, props.suffix) : null);
}

/** Page title block shared by both portals: optional eyebrow, title, lead and actions. */
export function PageHeader(props: { title: ReactNode; eyebrow?: ReactNode; lead?: ReactNode; actions?: ReactNode }) {
  return h(
    'header',
    { className: 'p-page-head' },
    h(
      'div',
      null,
      props.eyebrow ? h('span', { className: 'p-eyebrow' }, props.eyebrow) : null,
      h('h1', null, props.title),
      props.lead ? h('p', { className: 'p-lead' }, props.lead) : null,
    ),
    props.actions ? h('div', { className: 'p-row' }, props.actions) : null,
  );
}

/** One quiet row of trust indicators. */
export function TrustRow(props: { items: { icon: IconName; label: ReactNode }[]; label?: string }) {
  return h(
    'ul',
    { className: 'p-trust', 'aria-label': props.label ?? 'Trust and privacy' },
    props.items.map((it, i) => h('li', { key: i }, h(Icon, { name: it.icon, size: 15 }), it.label)),
  );
}

export function useAsyncAction() {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const run = useCallback(async <T,>(fn: () => Promise<T>): Promise<T | undefined> => {
    setBusy(true);
    setError(null);
    try {
      return await fn();
    } catch (e) {
      setError(e);
      return undefined;
    } finally {
      setBusy(false);
    }
  }, []);
  return { busy, error, run, setError };
}
